/**
 * Funnel runtime provider — session id, assign, evaluate-step, accept/decline, template fetch.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { createFunnelClient, FunnelApiError, type FunnelClientConfig } from './create-funnel-client';
import {
  EVENT_TYPES,
  publishBrowserBusinessEvent,
} from '@template-project/storefront-shared';
import type {
  DisplaySettings,
  FunnelEvaluateStepResponse,
  PageContext,
  StepNavigationResponse,
  TemplateContentResponse,
  FunnelEvaluateStepRequest,
} from './types';
import { FunnelAnalyticsProvider, mergeAnalytics, type FunnelAnalytics } from './analytics';
import { ProductImageProvider, type ResolveProductImage } from './product-image-context';
import { funnelLogger } from './logger';
import { buildCookieString } from './cookie-utils';
import { extractBannerPlacement } from './banner-placement';
import {
  FUNNEL_SESSION_ID_KEY as SESSION_ID_KEY,
  FUNNEL_SESSION_COUNT_KEY as SESSION_COUNT_KEY,
  ASSIGNED_FUNNEL_ID_KEY,
  ASSIGNED_FUNNEL_NAME_KEY,
  ASSIGNED_FUNNEL_TYPE_KEY,
  ASSIGNED_FUNNEL_EXPIRES_AT_KEY,
  DECLINED_STEPS_KEY,
  CURRENT_STEP_KEY_PREFIX,
  syncFunnelRuntimeVersion,
} from './runtime-storage';
import {
  attachAnalyticsBusAdapter,
  attachFunnelEventsDebugLogger,
  attachBehavioralAccumulator,
  createFunnelEventBus,
  createTransportAdapter,
  isFunnelUiEventsEnabled,
  isFunnelUiEventsTransportEnabled,
  type FunnelEventBus,
} from './events';
import type { createBehavioralAccumulator } from './events/behavioral-accumulator';
import type { StorefrontSemanticPorts } from './handlers/ports';
import {
  SemanticComponentCapabilityRegistry,
  type ComponentCapabilityHandler,
  type ComponentSemanticActionType,
} from './handlers/component-capability-registry';
import type { FunnelShowToast } from '@template-project/funnels-contract';

export type { FunnelShowToast };

const MAX_ASSIGN_RETRIES = 2;
const ASSIGN_TIMEOUT_MS = 15_000;
const COUNTRY_CURRENCY_MAP: Readonly<Record<string, string>> = {
  US: 'USD',
  CA: 'CAD',
  GB: 'GBP',
  AU: 'AUD',
  NL: 'EUR',
  SE: 'SEK',
  DE: 'EUR',
  DK: 'DKK',
  FI: 'EUR',
  IE: 'EUR',
  FR: 'EUR',
};

function resolveCustomerCurrency(
  configuredCountry: string | undefined,
  contextCountry: string | undefined,
): string | undefined {
  const locale =
    (typeof navigator !== 'undefined' ? navigator.language : null) || 'en-US';
  const navigationCountry = locale.split('-')[1]?.toUpperCase();
  const customerCountry =
    configuredCountry?.trim().toUpperCase() ||
    contextCountry?.trim().toUpperCase() ||
    navigationCountry;
  return customerCountry ? COUNTRY_CURRENCY_MAP[customerCountry] : undefined;
}

function getNavigationType(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const entries = window.performance?.getEntriesByType?.('navigation');
    const firstEntry = Array.isArray(entries) ? entries[0] : undefined;
    if (firstEntry && 'type' in firstEntry && typeof firstEntry.type === 'string') {
      return firstEntry.type;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function shouldForceFreshSessionOnLoad(): boolean {
  return Boolean(import.meta.env?.DEV) && getNavigationType() === 'reload';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string | null): void {
  if (typeof document === 'undefined') return;
  document.cookie = buildCookieString(name, value, window.location.hostname);
}

export interface AcceptOfferParams {
  stepId: string;
  /** Optional explicit retry key. Cart-bound accepts derive one from the cart and offer when absent. */
  idempotencyKey?: string;
  offerId?: string;
  productId?: string;
  variantId?: string;
  quantity?: number;
  cartToken?: string;
  action?: 'add_to_cart' | 'add_to_order';
  /** Required for post-purchase (add_to_order). The order ID from checkout completion. */
  orderId?: string;
}

export interface FunnelContextValue {
  config: FunnelClientConfig;
  sessionId: string;
  assignedFunnelId: string | null;
  assignedFunnelName: string | null;
  assignedFunnelType: string | null;
  isAssignedFunnelLocked: boolean;
  loading: boolean;
  assignError: string | null;
  assignPageFunnel: (pageContext: PageContext, signal?: AbortSignal) => Promise<{ funnelId: string; funnelName: string; funnelType?: string | null; cached: boolean }>;
  lockAssignedFunnel: (funnelId: string, funnelName?: string | null) => void;
  clearAssignedFunnel: () => void;
  evaluatePageStep: (request: Omit<FunnelEvaluateStepRequest, 'sessionId' | 'funnelId'> & { funnelId?: string }) => Promise<FunnelEvaluateStepResponse>;
  acceptOffer: (params: AcceptOfferParams) => Promise<StepNavigationResponse>;
  declineOffer: (stepId: string, orderId?: string) => Promise<StepNavigationResponse>;
  /**
   * D-C: server-validated go_to_step. Asks funnels-go to jump from `stepId` to
   * `targetStepId`; the server refuses unless the current step authored a
   * go_to_step reaction to that target.
   */
  navigateToStep: (stepId: string, targetStepId: string) => Promise<StepNavigationResponse>;
  fetchTemplate: (templateId: string) => Promise<TemplateContentResponse>;
  activeExperience: {
    funnelId: string;
    stepId: string;
    experienceType: string | null;
    placement?: string;
    displaySettings?: DisplaySettings | null;
    bannerPlacement?: ReturnType<typeof extractBannerPlacement>;
  } | null;
  setActiveExperience: (experience: {
    funnelId: string;
    stepId: string;
    experienceType: string | null;
    placement?: string;
    displaySettings?: DisplaySettings | null;
    bannerPlacement?: ReturnType<typeof extractBannerPlacement>;
  } | null) => void;
  experimentVariantKey: string | null;
  assignFunnelExperiments: (funnelId: string, signal?: AbortSignal) => Promise<string | null>;
  /** In-browser UI event bus (ADR 0004). */
  eventBus: FunnelEventBus;
  /** Fold behavioral accumulator into page context for evaluate-step. */
  enrichPageContext: (base: Partial<PageContext>) => Partial<PageContext>;
  /**
   * Resolve a full base page context (device, language, URL, url parameters,
   * session count, behavioral signals) for client-side hook evaluation.
   */
  resolvePageContext: () => Partial<PageContext>;
  /** Host-owned capabilities consumed by the centralized semantic dispatcher. */
  semanticPorts: StorefrontSemanticPorts;
  /**
   * Optional host toast bridge for `experienceType: "toast"`.
   * When absent, FunnelToast still dismisses the experience (no visual).
   */
  showToast?: FunnelShowToast;
  registerSemanticComponentCapability: (
    actionType: ComponentSemanticActionType,
    targetId: string,
    handler: ComponentCapabilityHandler,
  ) => () => void;
  registerSemanticHostPorts: (ports: StorefrontSemanticPorts) => () => void;
}

const FunnelContext = createContext<FunnelContextValue | undefined>(undefined);

function buildBaseContext(): Partial<PageContext> {
  if (typeof window === 'undefined') return {};
  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const now = new Date();

  const urlParameters: Record<string, string> = {};
  try {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      urlParameters[key] = value;
    });
  } catch {
    /* ignore */
  }

  let sessionCount = 0;
  try {
    sessionCount = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10) || 0;
  } catch {
    /* ignore */
  }

  return {
    deviceType: isMobile ? 'mobile' : 'desktop',
    browserLanguage: navigator.language,
    dayOfWeek: now.getDay(),
    timeOfDay: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
    currentDate: now.toISOString().slice(0, 10),
    landingUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
    sessionCount,
  };
}

function getCurrentURLPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const { pathname, search } = window.location;
  if (!pathname) return undefined;
  return `${pathname}${search ?? ''}`;
}

function withResolvedURL<T extends Partial<PageContext>>(pageContext: T): T & { url?: string } {
  return {
    ...pageContext,
    url: pageContext.url ?? getCurrentURLPath(),
  };
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
  }
  try {
    if (shouldForceFreshSessionOnLoad()) {
      const staleCurrentStepKeys: string[] = [];
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(CURRENT_STEP_KEY_PREFIX)) {
          staleCurrentStepKeys.push(key);
        }
      }

      const currentSessionCount = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10) || 0;
      sessionStorage.setItem(SESSION_COUNT_KEY, String(currentSessionCount + 1));

      const id = (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
      sessionStorage.setItem(SESSION_ID_KEY, id);
      // Keep funnel_assigned_id across dev reloads so landing → PDP → checkout journeys survive refresh.
      sessionStorage.removeItem(DECLINED_STEPS_KEY);
      for (const key of staleCurrentStepKeys) {
        sessionStorage.removeItem(key);
      }
      writeCookie(SESSION_ID_KEY, id);
      writeCookie(DECLINED_STEPS_KEY, null);
      if (typeof document !== 'undefined') {
        const rawCookies = document.cookie ? document.cookie.split('; ') : [];
        for (const cookie of rawCookies) {
          const name = cookie.split('=')[0];
          if (name.startsWith(CURRENT_STEP_KEY_PREFIX)) {
            writeCookie(name, null);
          }
        }
      }
      return id;
    }

    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;

    const cookieSessionId = readCookie(SESSION_ID_KEY);
    if (cookieSessionId) {
      sessionStorage.setItem(SESSION_ID_KEY, cookieSessionId);
      return cookieSessionId;
    }

    const currentSessionCount = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10) || 0;
    sessionStorage.setItem(SESSION_COUNT_KEY, String(currentSessionCount + 1));

    const id = (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
    sessionStorage.setItem(SESSION_ID_KEY, id);
    writeCookie(SESSION_ID_KEY, id);
    return id;
  } catch {
    return (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
  }
}

const EXPERIMENT_VARIANT_KEY = 'tf_funnel_experiment_variant';

function readStoredValue(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue) return sessionValue;
    const cookieValue = readCookie(key);
    if (cookieValue) {
      sessionStorage.setItem(key, cookieValue);
      return cookieValue;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredValue(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null || value === '') {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
    writeCookie(key, value);
  } catch {
    /* ignore */
  }
}

function readAssignedExpiry(): string | null {
  return readStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY);
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) return false;
  return parsed <= Date.now();
}

export interface FunnelProviderProps {
  children: ReactNode;
  /** Funnels API base URL and tenant (same as storefront-layout env). */
  config: FunnelClientConfig;
  /** Optional deploy/runtime fingerprint used to invalidate stale browser funnel state after updates. */
  runtimeVersion?: string | null;
  countryCode?: string;
  /** Optional dataLayer / analytics hooks (defaults to no-ops). */
  analytics?: Partial<FunnelAnalytics>;
  /** Optional: resolve product hero image when offer.imageUrl is empty. */
  resolveProductImage?: ResolveProductImage;
  /** Typed host/component capabilities; absent capabilities fail as unsupported. */
  semanticPorts?: StorefrontSemanticPorts;
  /** Optional host toast bridge for `experienceType: "toast"`. */
  showToast?: FunnelShowToast;
}

export function FunnelProvider({
  children,
  config,
  runtimeVersion,
  countryCode,
  analytics: analyticsPartial,
  resolveProductImage,
  semanticPorts,
  showToast,
}: FunnelProviderProps) {
  const stableConfig = useMemo<FunnelClientConfig>(
    () => ({
      apiBaseUrl: config.apiBaseUrl,
      tenantId: config.tenantId,
      checkoutMiddlewareUrl: config.checkoutMiddlewareUrl,
      ...(config.storeId ? { storeId: config.storeId } : {}),
    }),
    [config.apiBaseUrl, config.tenantId, config.checkoutMiddlewareUrl, config.storeId],
  );

  const client = useMemo(
    () => createFunnelClient(stableConfig),
    [stableConfig],
  );

  useState(() => {
    syncFunnelRuntimeVersion(runtimeVersion);
    return true;
  });

  const [sessionId] = useState(getOrCreateSessionId);
  const [assignedFunnelId, setAssignedFunnelId] = useState<string | null>(() => {
    const expiresAt = readAssignedExpiry();
    if (isExpired(expiresAt)) {
      writeStoredValue(ASSIGNED_FUNNEL_ID_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_NAME_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_TYPE_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, null);
      return null;
    }
    return readStoredValue(ASSIGNED_FUNNEL_ID_KEY);
  });
  const [assignedFunnelName, setAssignedFunnelName] = useState<string | null>(() => {
    const expiresAt = readAssignedExpiry();
    if (isExpired(expiresAt)) {
      return null;
    }
    return readStoredValue(ASSIGNED_FUNNEL_NAME_KEY);
  });
  const [assignedFunnelType, setAssignedFunnelType] = useState<string | null>(() => {
    if (isExpired(readAssignedExpiry())) {
      return null;
    }
    return readStoredValue(ASSIGNED_FUNNEL_TYPE_KEY);
  });
  const [assignedFunnelExpiresAt, setAssignedFunnelExpiresAt] = useState<string | null>(() => {
    const expiresAt = readAssignedExpiry();
    return isExpired(expiresAt) ? null : expiresAt;
  });
  const [isAssignedFunnelLocked, setIsAssignedFunnelLocked] = useState(() => (
    Boolean(readStoredValue(ASSIGNED_FUNNEL_ID_KEY)) && !isExpired(readAssignedExpiry())
  ));
  const [loading, setLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [activeExperience, setActiveExperience] = useState<FunnelContextValue['activeExperience']>(null);
  const [experimentVariantKey, setExperimentVariantKey] = useState<string | null>(
    () => readStoredValue(EXPERIMENT_VARIANT_KEY),
  );
  const assignedFunnelMetadataRef = useRef({
    id: assignedFunnelId,
    name: assignedFunnelName,
    type: assignedFunnelType,
  });

  const eventBusRef = useRef(createFunnelEventBus());
  const behavioralRef = useRef<ReturnType<typeof createBehavioralAccumulator> | null>(null);
  const componentCapabilityRegistryRef = useRef(
    new SemanticComponentCapabilityRegistry(),
  );
  const [registeredSemanticHostPorts, setRegisteredSemanticHostPorts] =
    useState<StorefrontSemanticPorts[]>([]);

  const registerSemanticHostPorts = useCallback(
    (ports: StorefrontSemanticPorts) => {
      setRegisteredSemanticHostPorts((current) => [...current, ports]);
      return () => {
        setRegisteredSemanticHostPorts((current) =>
          current.filter((candidate) => candidate !== ports),
        );
      };
    },
    [],
  );

  const registerSemanticComponentCapability = useCallback(
    (
      actionType: ComponentSemanticActionType,
      targetId: string,
      handler: ComponentCapabilityHandler,
    ) =>
      componentCapabilityRegistryRef.current.register(
        actionType,
        targetId,
        handler,
      ),
    [],
  );

  const componentSemanticPorts = useMemo<StorefrontSemanticPorts>(
    () => ({
      revealCoupon: (input, context) =>
        componentCapabilityRegistryRef.current.execute(
          'reveal_coupon',
          input.targetBlockId,
          input,
          context,
        ),
      runSpin: (input, context) =>
        componentCapabilityRegistryRef.current.execute(
          'run_spin',
          input.targetBlockId ?? input.wheelId,
          input,
          context,
        ),
      dismissExperience: (input, context) =>
        componentCapabilityRegistryRef.current.execute(
          'dismiss_experience',
          input.targetBlockId ?? input.experienceId,
          input,
          context,
        ),
      selectVariant: (input, context) =>
        componentCapabilityRegistryRef.current.execute(
          'select_variant',
          input.targetBlockId,
          input,
          context,
        ),
    }),
    [],
  );

  const effectiveSemanticPorts = useMemo(
    () => ({
      ...componentSemanticPorts,
      ...Object.assign({}, ...registeredSemanticHostPorts),
      ...semanticPorts,
    }),
    [componentSemanticPorts, registeredSemanticHostPorts, semanticPorts],
  );

  // Dispose the bus on unmount to prevent EventTarget leaks across HMR cycles.
  // Strict Mode re-runs cleanup→setup on the same ref; reactivate so emit works.
  useEffect(() => {
    const bus = eventBusRef.current;
    bus.reactivate();
    return () => { bus.dispose(); };
  }, []);

  const analyticsValue = useMemo(() => mergeAnalytics(analyticsPartial), [analyticsPartial]);

  useEffect(() => {
    if (!isFunnelUiEventsEnabled()) return undefined;
    const bus = eventBusRef.current;
    bus.reactivate();
    const analyticsSub = attachAnalyticsBusAdapter(bus, analyticsValue);
    // Dev-only visibility: console.log every emission (flag defaults OFF; dev env-config turns it on).
    const debugSub = attachFunnelEventsDebugLogger(bus);
    const { unsubscribe: behavioralUnsub, accumulator } = attachBehavioralAccumulator(bus);
    behavioralRef.current = accumulator;
    const transportSub = createTransportAdapter(bus, {
      apiBaseUrl: stableConfig.apiBaseUrl,
      // Use the explicit storeId when configured, otherwise fall back to
      // tenantId — store_id is required by the BFF analytics batch schema
      // (z.string().min(1)), and the BFF resolves the trusted value from the
      // authenticated tenant scope regardless of what the client sends.
      storeId: stableConfig.storeId || stableConfig.tenantId,
      // Runtime-flippable (query param > window.__ENV__ > build env) so the
      // transport can be toggled at deploy time without a rebuild.
      enabled: isFunnelUiEventsTransportEnabled(),
    });
    return () => {
      analyticsSub.unsubscribe();
      debugSub.unsubscribe();
      behavioralUnsub();
      transportSub.unsubscribe();
    };
  }, [analyticsValue, stableConfig.apiBaseUrl, stableConfig.storeId]);

  const enrichPageContext = useCallback((base: Partial<PageContext>): Partial<PageContext> => {
    if (!isFunnelUiEventsEnabled() || !behavioralRef.current) {
      return base;
    }
    return behavioralRef.current.foldIntoPageContext(base);
  }, []);

  const resolvePageContext = useCallback(
    (): Partial<PageContext> => withResolvedURL(enrichPageContext(buildBaseContext())),
    [enrichPageContext],
  );

  const setAssignedState = useCallback((
    funnelId: string | null,
    funnelName: string | null,
    locked: boolean,
    expiresAt?: string | null,
    funnelType?: string | null,
  ) => {
    assignedFunnelMetadataRef.current = {
      id: funnelId,
      name: funnelName,
      type: funnelType ?? null,
    };
    setAssignedFunnelId(funnelId);
    setAssignedFunnelName(funnelName);
    setAssignedFunnelType(funnelType ?? null);
    setAssignedFunnelExpiresAt(expiresAt ?? null);
    setIsAssignedFunnelLocked(locked);
    if (funnelId) {
      writeStoredValue(ASSIGNED_FUNNEL_ID_KEY, funnelId);
      writeStoredValue(ASSIGNED_FUNNEL_NAME_KEY, funnelName);
      writeStoredValue(ASSIGNED_FUNNEL_TYPE_KEY, funnelType ?? null);
      writeStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, expiresAt ?? null);
    } else {
      writeStoredValue(ASSIGNED_FUNNEL_ID_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_NAME_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_TYPE_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, null);
    }
  }, []);

  const lockAssignedFunnel = useCallback((funnelId: string, funnelName?: string | null) => {
    setAssignedState(
      funnelId,
      funnelName ?? assignedFunnelName ?? null,
      true,
      assignedFunnelExpiresAt,
      assignedFunnelType,
    );
  }, [assignedFunnelExpiresAt, assignedFunnelName, assignedFunnelType, setAssignedState]);

  const clearAssignedFunnel = useCallback(() => {
    setAssignedState(null, null, false);
  }, [setAssignedState]);

  const ensureAssignedFunnelNotExpired = useCallback((): boolean => {
    if (!assignedFunnelId || !isExpired(assignedFunnelExpiresAt)) {
      return false;
    }
    setAssignedState(null, null, false, null);
    return true;
  }, [assignedFunnelExpiresAt, assignedFunnelId, setAssignedState]);

  const assignFunnelExperiments = useCallback(
    async (funnelId: string, signal?: AbortSignal): Promise<string | null> => {
      if (!funnelId) return null;
      try {
        const res = await client.assignExperiments(funnelId, sessionId, signal);
        const funnelScoped = res.assignments.find((a) => a.scope !== 'step');
        const chosen = funnelScoped ?? res.assignments[0] ?? null;
        const variantKey = chosen?.variantKey ?? null;
        setExperimentVariantKey(variantKey);
        writeStoredValue(EXPERIMENT_VARIANT_KEY, variantKey);
        return variantKey;
      } catch (err) {
        // Experiments are non-essential; never fail funnel assignment over them.
        funnelLogger.warn('Experiment assignment failed', {
          funnelId,
          reason: String(err),
        });
        return null;
      }
    },
    [client, sessionId],
  );

  const assignPageFunnel = useCallback(
    async (pageContext: PageContext, signal?: AbortSignal): Promise<{ funnelId: string; funnelName: string; funnelType: string | null; cached: boolean }> => {
      const baseCtx = buildBaseContext();
      const mergedContext: PageContext = withResolvedURL({ ...baseCtx, ...pageContext });

      const locale = (typeof window !== 'undefined' ? navigator.language : null) || 'en-US';
      const navCountry = locale.split('-')[1]?.toUpperCase();
      const customerCountry = countryCode ?? mergedContext.country ?? navCountry;
      const customerCurrency = resolveCustomerCurrency(countryCode, mergedContext.country);
      const deviceType = mergedContext.deviceType ?? (/Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop');
      const referrerSource = typeof document !== 'undefined' ? document.referrer || undefined : undefined;

      setAssignError(null);
      setLoading(true);

      for (let attempt = 0; attempt <= MAX_ASSIGN_RETRIES; attempt++) {
        try {
          const controller = signal ? null : new AbortController();
          const activeSignal = signal ?? controller?.signal;
          const timeoutId = controller ? setTimeout(() => controller.abort(), ASSIGN_TIMEOUT_MS) : null;

          const result = await client.assignFunnel(
            {
              sessionId,
              customerCurrency,
              customerCountry,
              urlPath: mergedContext.url,
              pageType: mergedContext.pageType,
              pageContext: mergedContext,
              deviceType,
              referrerSource,
            },
            activeSignal,
          );

          if (timeoutId) clearTimeout(timeoutId);

          if (result.funnelId) {
            // Lock session funnels as soon as they are assigned on the entry page so
            // later pages (PDP, cart, checkout) cannot wipe the journey before add-to-cart.
            const lockAssignment =
              result.cached ||
              mergedContext.pageType === 'landing_page' ||
              mergedContext.pageType === 'post_purchase';
            setAssignedState(
              result.funnelId,
              result.funnelName ?? null,
              lockAssignment,
              result.expiresAt ?? null,
              result.funnelType ?? null,
            );
            void assignFunnelExperiments(result.funnelId, signal);
            funnelLogger.info('Funnel assigned for page', {
              funnelId: result.funnelId,
              funnelName: result.funnelName ?? undefined,
              funnelType: result.funnelType ?? null,
              cached: result.cached,
              pageType: mergedContext.pageType,
            });
          } else {
            funnelLogger.warn('No funnel was assigned for this page', {
              pageType: mergedContext.pageType,
              urlPath: mergedContext.url ?? null,
              customerCountry: customerCountry ?? null,
              customerCurrency: customerCurrency ?? null,
              reason: 'no_matching_funnel_or_entry_step',
            });
            const existingAssignedId = readStoredValue(ASSIGNED_FUNNEL_ID_KEY);
            if (!existingAssignedId) {
              clearAssignedFunnel();
            } else {
              funnelLogger.info('Preserving existing funnel assignment after empty assign', {
                pageType: mergedContext.pageType,
                funnelId: existingAssignedId,
                urlPath: mergedContext.url ?? null,
              });
            }
          }

          setLoading(false);
          return {
            funnelId: result.funnelId ?? '',
            funnelName: result.funnelName ?? '',
            funnelType: result.funnelType ?? null,
            cached: result.cached,
          };
        } catch (err) {
          if (err instanceof FunnelApiError && err.status === 404) {
            funnelLogger.info('Funnel assign returned no active funnels', {
              status: 404,
              pageType: mergedContext.pageType,
              urlPath: mergedContext.url ?? null,
            });
            const existingAssignedId = readStoredValue(ASSIGNED_FUNNEL_ID_KEY);
            if (!existingAssignedId) {
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.removeItem(SESSION_ID_KEY);
              }
              clearAssignedFunnel();
            } else {
              funnelLogger.info('Preserving existing funnel assignment after assign 404', {
                pageType: mergedContext.pageType,
                funnelId: existingAssignedId,
                urlPath: mergedContext.url ?? null,
              });
            }
            setLoading(false);
            return {
              funnelId: existingAssignedId ?? '',
              funnelName: readStoredValue(ASSIGNED_FUNNEL_NAME_KEY) ?? '',
              funnelType: readStoredValue(ASSIGNED_FUNNEL_TYPE_KEY),
              cached: Boolean(existingAssignedId),
            };
          }
          if (attempt === MAX_ASSIGN_RETRIES) {
            funnelLogger.error('Funnel assign failed after retries', err instanceof Error ? err : undefined, {
              assignError: String(err),
              pageType: mergedContext.pageType,
              urlPath: mergedContext.url ?? null,
              reason: 'assign_request_failed',
            });
            setAssignError(String(err));
            setLoading(false);
            throw err;
          }
          await new Promise<void>((r) => setTimeout(r, 1000 * (attempt + 1)));
        }
      }

      setLoading(false);
      return { funnelId: '', funnelName: '', funnelType: null, cached: false };
    },
    [assignFunnelExperiments, clearAssignedFunnel, client, countryCode, sessionId, setAssignedState],
  );

  const evaluatePageStep = useCallback(
    async (request: Omit<FunnelEvaluateStepRequest, 'sessionId' | 'funnelId'> & { funnelId?: string }): Promise<FunnelEvaluateStepResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { hasExperience: false, step: null };
      }
      const activeFunnelId = request.funnelId ?? assignedFunnelId;
      if (!activeFunnelId) {
        return { hasExperience: false, step: null };
      }
      const baseCtx = buildBaseContext();
      const resolvedPageContext = withResolvedURL(
        enrichPageContext({ ...baseCtx, ...request.pageContext }),
      ) as PageContext;
      const evaluationStartedAt = Date.now();
      const result = await client.evaluateStep({
        sessionId,
        funnelId: activeFunnelId,
        customerCurrency: resolveCustomerCurrency(countryCode, resolvedPageContext.country),
        currentStepId: request.currentStepId,
        event: request.event,
        pageContext: resolvedPageContext,
      });
      const assignedMetadata = assignedFunnelMetadataRef.current;
      const activeFunnelName = assignedMetadata.id === activeFunnelId
        ? assignedMetadata.name
        : assignedFunnelName;
      const activeFunnelType = assignedMetadata.id === activeFunnelId
        ? assignedMetadata.type
        : assignedFunnelType;
      publishBrowserBusinessEvent(EVENT_TYPES.FUNNEL_EVALUATED, {
        funnel_id: activeFunnelId,
        funnel_name: activeFunnelName || activeFunnelId,
        funnel_type: activeFunnelType ?? 'unknown',
        matched: result.hasExperience,
        step_count: result.hasExperience && result.step ? 1 : 0,
        evaluation_ms: Math.max(0, Date.now() - evaluationStartedAt),
      });
      return result;
    },
    [sessionId, assignedFunnelId, assignedFunnelName, assignedFunnelType, client, countryCode, ensureAssignedFunnelNotExpired, enrichPageContext],
  );

  const acceptOffer = useCallback(
    async (params: AcceptOfferParams): Promise<StepNavigationResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { complete: true, nextStep: null };
      }
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null };
      }
      if (params.action === 'add_to_order') {
        if (!params.orderId) {
          throw new Error('[FunnelProvider] add_to_order requires orderId — this is a caller bug');
        }
        return client.acceptUpsell({
          funnelId: assignedFunnelId,
          stepId: params.stepId,
          orderId: params.orderId,
        });
      }
      return client.acceptStep({
        funnelId: assignedFunnelId,
        stepId: params.stepId,
        sessionId,
        idempotencyKey:
          params.idempotencyKey ??
          (params.cartToken
            ? `cart-offer:${params.cartToken}:${params.stepId}:${params.offerId ?? 'default'}`
            : undefined),
        action: params.action ?? 'add_to_cart',
        offerId: params.offerId,
        productId: params.productId,
        variantId: params.variantId,
        quantity: params.quantity,
        cartToken: params.cartToken,
      });
    },
    [sessionId, assignedFunnelId, client, ensureAssignedFunnelNotExpired],
  );

  const declineOffer = useCallback(
    async (stepId: string, orderId?: string): Promise<StepNavigationResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { complete: true, nextStep: null };
      }
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null };
      }
      if (orderId) {
        return client.declineUpsell({
          funnelId: assignedFunnelId,
          stepId,
        });
      }
      return client.declineStep({
        funnelId: assignedFunnelId,
        stepId,
        sessionId,
      });
    },
    [sessionId, assignedFunnelId, client, ensureAssignedFunnelNotExpired],
  );

  const navigateToStep = useCallback(
    async (stepId: string, targetStepId: string): Promise<StepNavigationResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { complete: true, nextStep: null };
      }
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null };
      }
      return client.navigateStep({
        funnelId: assignedFunnelId,
        stepId,
        targetStepId,
        sessionId,
      });
    },
    [sessionId, assignedFunnelId, client, ensureAssignedFunnelNotExpired],
  );

  const fetchTemplate = useCallback(
    (templateId: string) => client.fetchTemplate(templateId),
    [client],
  );

  const contextValue: FunnelContextValue = useMemo(
    () => ({
      config: stableConfig,
      sessionId,
      assignedFunnelId,
      assignedFunnelName,
      assignedFunnelType,
      isAssignedFunnelLocked,
      loading,
      assignError,
      assignPageFunnel,
      lockAssignedFunnel,
      clearAssignedFunnel,
      evaluatePageStep,
      acceptOffer,
      declineOffer,
      navigateToStep,
      fetchTemplate,
      activeExperience,
      setActiveExperience,
      experimentVariantKey,
      assignFunnelExperiments,
      eventBus: eventBusRef.current,
      enrichPageContext,
      resolvePageContext,
      semanticPorts: effectiveSemanticPorts,
      showToast,
      registerSemanticComponentCapability,
      registerSemanticHostPorts,
    }),
    [
      stableConfig,
      sessionId,
      assignedFunnelId,
      assignedFunnelName,
      assignedFunnelType,
      isAssignedFunnelLocked,
      loading,
      assignError,
      assignPageFunnel,
      lockAssignedFunnel,
      clearAssignedFunnel,
      evaluatePageStep,
      acceptOffer,
      declineOffer,
      navigateToStep,
      fetchTemplate,
      experimentVariantKey,
      assignFunnelExperiments,
      enrichPageContext,
      resolvePageContext,
      effectiveSemanticPorts,
      showToast,
      registerSemanticComponentCapability,
      registerSemanticHostPorts,
      activeExperience,
    ],
  );

  return (
    <FunnelAnalyticsProvider value={analyticsValue}>
      <ProductImageProvider resolveProductImage={resolveProductImage}>
        <FunnelContext.Provider value={contextValue}>{children}</FunnelContext.Provider>
      </ProductImageProvider>
    </FunnelAnalyticsProvider>
  );
}

export function useFunnelContext(): FunnelContextValue {
  const context = useContext(FunnelContext);
  if (context === undefined) {
    throw new Error('useFunnelContext must be used within a FunnelProvider');
  }
  return context;
}

export function useOptionalFunnelContext(): FunnelContextValue | null {
  return useContext(FunnelContext) ?? null;
}
